import type { World } from './world.js';
import type { EngineConfig } from '../types.js';
import { clamp } from '../math.js';

/**
 * Integrate positions by velocity * dt for all movable entities.
 * Does NOT apply AI or firing — those run in their own systems.
 */
export function updateMovement(world: World, dtMs: number, config: EngineConfig): void {
  const dt = dtMs / 1000;

  // Players — integrate + clamp to world bounds.
  for (const p of world.players) {
    if (!p.alive) continue;
    p.pos.x += p.vel.x * dt;
    p.pos.y += p.vel.y * dt;
    p.pos.x = clamp(p.pos.x, p.radius, config.worldWidth - p.radius);
    p.pos.y = clamp(p.pos.y, p.radius, config.worldHeight - p.radius);
    if (p.invulnMs > 0) p.invulnMs = Math.max(0, p.invulnMs - dtMs);
    if (p.respawnMs > 0) p.respawnMs = Math.max(0, p.respawnMs - dtMs);
  }

  // Enemies — movement is applied by AI system, but flash decay lives here.
  for (const e of world.enemies) {
    if (!e.alive) continue;
    if (e.flashMs > 0) e.flashMs = Math.max(0, e.flashMs - dtMs);
  }

  // Bullets
  for (const b of world.bullets) {
    if (!b.alive) continue;
    b.pos.x += b.vel.x * dt;
    b.pos.y += b.vel.y * dt;
    b.ageMs += dtMs;
    if (b.armMs > 0) b.armMs = Math.max(0, b.armMs - dtMs);
  }

  // Pickups
  for (const pk of world.pickups) {
    if (!pk.alive) continue;
    pk.pos.x += pk.vel.x * dt;
    pk.pos.y += pk.vel.y * dt;
    pk.ageMs += dtMs;
  }

  // Particles decay by age.
  for (const pt of world.particles) {
    if (!pt.alive) continue;
    pt.pos.x += pt.vel.x * dt;
    pt.pos.y += pt.vel.y * dt;
    pt.ageMs += dtMs;
    if (pt.ageMs >= pt.lifeMs) pt.alive = false;
  }

  // Boss position is driven by its own controller; only decay flash here.
  if (world.boss && world.boss.alive) {
    if (world.boss.flashMs > 0) {
      world.boss.flashMs = Math.max(0, world.boss.flashMs - dtMs);
    }
  }

  // Camera decay.
  if (world.shake > 0) {
    world.shake = Math.max(0, world.shake - dtMs * 0.04);
  }
  if (world.hitStopMs > 0) {
    world.hitStopMs = Math.max(0, world.hitStopMs - dtMs);
  }
}
